/**
 * Weibull Analysis - Plotting Position Utilities
 * 
 * This module provides standardized plotting position calculations using 
 * Johnson's method with proper censoring handling for Weibull analysis.
 */

import { FailureRecord } from '@/types/failure';
import { isCensoredObservation } from '@/utils/weibullCensored';

// Re-export from weibullCensored for consistency
export { isCensoredObservation } from '@/utils/weibullCensored';

export interface PlottingDataPoint {
  time: number;
  isCensored: boolean;
  originalRecord: FailureRecord;
  adjustedRank?: number;
  medianRank?: number;
}

export interface PlottingPositionResult {
  times: number[];
  probabilities: number[];
  records: FailureRecord[];
  metadata: {
    totalSamples: number;
    failureCount: number;
    censoredCount: number;
    censoringPercentage: number;
    method: string;
  };
}


/**
 * Calculate adjusted ranks using Johnson's method
 */
function calculateAdjustedRanks(sortedData: PlottingDataPoint[]): number[] {
  const n = sortedData.length;
  const adjustedRanks = new Array(n).fill(0);
  let previousAdjustedRank = 0;
  let currentRankIncrement = 0;


  for (let i = 0; i < n; i++) {
    if (!sortedData[i].isCensored) {
      // This is a failure - calculate adjusted rank
      const remainingItems = n - i;
      currentRankIncrement = (n + 1 - previousAdjustedRank) / (remainingItems + 1);
      previousAdjustedRank = previousAdjustedRank + currentRankIncrement;
      adjustedRanks[i] = previousAdjustedRank;
    }
  }

  return adjustedRanks;
}

/**
 * Calculate median ranks from adjusted ranks using Benard's approximation
 */
function calculateMedianRanks(
  adjustedRanks: number[],
  totalSamples: number,
  censoringPercentage: number
): number[] {
  const medianRanks = new Array(adjustedRanks.length).fill(0);
  

  for (let i = 0; i < adjustedRanks.length; i++) {
    if (adjustedRanks[i] > 0) {
      // Use appropriate median rank formula based on censoring level
      let medianRank: number;
      if (censoringPercentage > 30) {
        medianRank = (adjustedRanks[i] - 0.5) / totalSamples;
      } else {
        medianRank = (adjustedRanks[i] - 0.3) / (totalSamples + 0.4);
      }
      medianRanks[i] = medianRank;
    }
  }

  return medianRanks;
}

/**
 * Standardized plotting position calculation for Weibull analysis
 * Uses Johnson's method with proper censoring handling
 */
export function calculatePlottingPositions(
  failureRecords: FailureRecord[],
  method = 'Standard Johnsons Method'
): PlottingPositionResult {
  // Input validation
  if (!failureRecords || failureRecords.length === 0) {
    throw new Error('No failure records provided for plotting position calculation');
  }

  // Create plotting data points and filter invalid times
  const plottingData: PlottingDataPoint[] = failureRecords
    .map(record => ({
      time: record.operation_time_years,
      isCensored: isCensoredObservation(record),
      originalRecord: record
    }))
    .filter(point => point.time > 0)  // Filter out invalid times like weibullCensored.ts
    .sort((a, b) => a.time - b.time);

  const totalSamples = plottingData.length;
  const censoredCount = plottingData.filter(d => d.isCensored).length;
  const failureCount = totalSamples - censoredCount;
  const censoringPercentage = (censoredCount / totalSamples) * 100;


  // Calculate adjusted ranks
  const adjustedRanks = calculateAdjustedRanks(plottingData);
  
  // Calculate median ranks
  const medianRanks = calculateMedianRanks(adjustedRanks, totalSamples, censoringPercentage);

  // Extract results for failures only
  const times: number[] = [];
  const probabilities: number[] = [];
  const records: FailureRecord[] = [];

  for (let i = 0; i < plottingData.length; i++) {
    if (!plottingData[i].isCensored && medianRanks[i] > 0) {
      times.push(plottingData[i].time);
      probabilities.push(medianRanks[i]);
      records.push(plottingData[i].originalRecord);
    }
  }

  const result: PlottingPositionResult = {
    times,
    probabilities,
    records,
    metadata: {
      totalSamples,
      failureCount,
      censoredCount,
      censoringPercentage,
      method,
    }
  };


  return result;
}


/**
 * Validate plotting position calculation for production use
 */
export function validatePlottingPositions(result: PlottingPositionResult): {
  isValid: boolean;
  warnings: string[];
  recommendations: string[];
} {
  const warnings: string[] = [];
  const recommendations: string[] = [];
  let isValid = true;
  
  // Check data consistency
  if (result.times.length !== result.probabilities.length || 
      result.times.length !== result.records.length) {
    warnings.push('Array length mismatch in plotting position results');
    isValid = false;
  }
  
  // Check sorting
  for (let i = 1; i < result.times.length; i++) {
    if (result.times[i] < result.times[i-1]) {
      warnings.push('Times are not properly sorted');
      break;
    }
    if (result.probabilities[i] < result.probabilities[i-1]) {
      warnings.push('Probabilities are not monotonically increasing');
      break;
    }
  }
  
  // Check probability bounds
  const minProb = Math.min(...result.probabilities);
  const maxProb = Math.max(...result.probabilities);
  
  if (minProb <= 0 || maxProb >= 1) {
    warnings.push('Probabilities outside valid range (0, 1)');
    isValid = false;
  }
  
  if (minProb < 0.001 || maxProb > 0.999) {
    recommendations.push('Extreme probabilities detected - check for outliers');
  }
  
  // Check censoring level
  if (result.metadata.censoringPercentage > 80) {
    recommendations.push('Very high censoring (>80%) - consider data collection improvement');
  }
  
  // Check sample size
  if (result.metadata.failureCount < 10) {
    warnings.push('Small failure count (<10) - results may be unreliable');
  }
  
  return { isValid, warnings, recommendations };
}
